import Phaser from 'phaser';
import { SCENES, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS, COLORS_HEX, DEPTH } from '../constants';
import { UIPanel } from '../ui/UIPanel';
import { SceneRouter } from '../systems/SceneRouter';
import { InputRouter } from '../systems/InputRouter';
import { AudioRouter } from '../systems/AudioRouter';
import { GameState, GameMode } from '../state/GameState';
import { ProgressionSystem } from '../systems/ProgressionSystem';
import { SaveManager, SaveSlotInfo } from '../systems/SaveManager';
import { ProgressState } from '../state/ProgressState';
import { UIButton } from '../ui/UIButton';
import { UIText } from '../ui/UIText';
import { AuthService } from '../systems/AuthService';
import { AuthUI } from '../systems/AuthUI';

// ============================================
// MenuScene (Step 11 Integration - Mode Selection)
// ============================================

// Right-side menu column X (logo is on the left)
const MENU_PANEL_X = 1280;

// Chase animation: one of 4 players is chosen at random each run; monster types with frame counts, timing
const CHASE_PLAYER_NUMBERS = [1, 2, 3, 4];
function getChasePlayerRunKeys(playerNum: number): string[] {
  return [`player-${playerNum}-run-1`, `player-${playerNum}-run-2`, `player-${playerNum}-run-3`, `player-${playerNum}-run-4`];
}
const CHASE_MONSTER_TYPES: Array<{ type: string; frameCount: number }> = [
  { type: 'crawler', frameCount: 2 }, { type: 'runner', frameCount: 2 }, { type: 'brute', frameCount: 4 },
  { type: 'shielded', frameCount: 3 }, { type: 'bat', frameCount: 2 }, { type: 'flyer', frameCount: 3 },
  { type: 'juggernaut', frameCount: 4 }, { type: 'swarm', frameCount: 3 },
];
const CHASE_Y = 860;
const CHASE_DURATION_MS = 4160;     // Base duration; 30% slower than 3200
const CHASE_FIRST_DELAY_MS = 15000;
const CHASE_INTERVAL_MS = 30000;
const PLAYER_FRAME_MS = 75;        // 4 run frames, half of previous 2-frame interval
const MONSTER_FRAME_MS = 200;
const CHASE_PLAYER_SCALE = 0.294;  // 30% smaller than 0.42
const CHASE_MONSTER_SCALE = 0.5;   // 2x original 0.25
const CHASE_MONSTER_START_OFFSET = 1520; // Doubled again; monster starts far behind player
const CHASE_MONSTER_DURATION_MULT = 1.02; // Monster slightly slower (never catches up)
const CHASE_OFFSCREEN_MARGIN = 180;      // Both start/end fully off screen
const CHASE_DEPTH_BEHIND_UI = 1000;      // Behind menu panels/buttons (UI is 1200+)

export class MenuScene extends Phaser.Scene {
  private buttons: UIButton[] = [];
  private selectedIndex: number = 0;
  private modeText!: Phaser.GameObjects.Text;
  private currentMode: GameMode = '1P';
  private saveSlotText!: Phaser.GameObjects.Text;
  private loadOverlay?: Phaser.GameObjects.Container;
  private controlsOverlay?: Phaser.GameObjects.Container;
  private controlsPanel?: UIPanel;
  private controlsBackButton?: UIButton;
  private controllerWarningContainer?: Phaser.GameObjects.Container;

  // Account button (top-left corner)
  private accountContainer?: Phaser.GameObjects.Container;
  private accountProfileImage?: Phaser.GameObjects.Image;
  private accountText?: Phaser.GameObjects.Text;
  private authUnsubscribe?: () => void;
  private loginDancerSprite: Phaser.GameObjects.Image | null = null;
  private loginDancerFrameIndex: number = 0;
  private loginDancerTimer: number = 0;
  private loginDancerCycleCount: number = 0;
  private loginDancerFlipped: boolean = false;
  private static readonly LOGIN_DANCE_FRAMES_PER_CYCLE = 4;
  private static readonly LOGIN_DANCE_CYCLES_BEFORE_FLIP = 4;

  // Chase animation state
  private chaseNextDirection: number = 1; // 1 = right-to-left, -1 = left-to-right
  private chasePlayerSprite: Phaser.GameObjects.Image | null = null;
  private chaseMonsterSprite: Phaser.GameObjects.Image | null = null;
  private chaseMonsterType: string = '';
  private chaseMonsterFrameCount: number = 2;
  private chasePlayerRunKeys: string[] = getChasePlayerRunKeys(1); // current run keys (random player each chase)
  private chasePlayerFrameTimer: number = 0;
  private chaseMonsterFrameTimer: number = 0;
  private chasePlayerFrameIndex: number = 0;
  private chaseMonsterFrameIndex: number = 0;

  // Ignore confirm (INTERACT/START) for a short time after create to avoid carrying over a previous button press
  private static readonly MENU_INPUT_DELAY_MS = 400;
  private menuInputReadyAt: number = 0;

  constructor() {
    super({ key: SCENES.MENU });
  }

  create(): void {
    console.log('[MenuScene] Creating main menu...');
    SceneRouter.setCurrentScene(SCENES.MENU);
    InputRouter.init(this);
    InputRouter.setMenuMode(true); // Higher deadzone for menu navigation
    SaveManager.init();
    this.menuInputReadyAt = this.time.now + MenuScene.MENU_INPUT_DELAY_MS;

    AudioRouter.startMenuMusic(this);

    // Menu background (behind everything, including chase animation)
    if (this.textures.exists('menu-background')) {
      const bg = this.add.image(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, 'menu-background');
      bg.setDisplaySize(DESIGN_WIDTH, DESIGN_HEIGHT);
      bg.setOrigin(0.5);
      bg.setDepth(-1);
    }

    // Logo on the left
    const logoX = 380;
    const logoY = DESIGN_HEIGHT / 2;
    const logoMaxWidth = 420;
    const logoImg = this.add.image(logoX, logoY, 'logo');
    logoImg.setOrigin(0.5);
    const scale = Math.min(1, logoMaxWidth / logoImg.width);
    logoImg.setScale(scale);

    // Current save slot display (right side)
    this.createSaveSlotDisplay();

    // Mode selector
    this.createModeSelector();

    // Menu buttons
    this.createButtons();

    // Chase animation (player runs across screen chased by monster)
    this.setupChaseAnimation();

    // Controller detection indicator
    this.createControllerIndicator();

    // Account button (top-right, above controller indicator)
    this.createAccountButton();

    this.selectedIndex = 0;
    this.updateSelection();
  }

  private createModeSelector(): void {
    const baseY = 458; // Vertically centered with logo (DESIGN_HEIGHT/2)

    // Mode label
    this.add.text(MENU_PANEL_X, baseY, 'GAME MODE', {
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);

    // Mode selector background
    const modeBg = this.add.rectangle(MENU_PANEL_X, baseY + 40, 200, 50, COLORS.BG_MEDIUM, 0.8);
    modeBg.setStrokeStyle(2, COLORS.PRIMARY);

    // Arrows
    const leftArrow = this.add.text(MENU_PANEL_X - 80, baseY + 40, '◀', {
      fontSize: '24px',
      color: '#ffffff',
    }).setOrigin(0.5);
    leftArrow.setInteractive({ useHandCursor: true });
    leftArrow.on('pointerdown', () => this.toggleMode());

    const rightArrow = this.add.text(MENU_PANEL_X + 80, baseY + 40, '▶', {
      fontSize: '24px',
      color: '#ffffff',
    }).setOrigin(0.5);
    rightArrow.setInteractive({ useHandCursor: true });
    rightArrow.on('pointerdown', () => this.toggleMode());

    // Mode text
    this.modeText = this.add.text(MENU_PANEL_X, baseY + 40, '1 PLAYER', {
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.modeText.setOrigin(0.5);

    // Mode description (secondary text)
    this.add.text(MENU_PANEL_X, baseY + 75, 'Use ← → to change mode', {
      fontSize: '12px',
      color: COLORS_HEX.TEXT_SECONDARY,
    }).setOrigin(0.5);
  }

  private toggleMode(): void {
    const order: GameMode[] = ['1P', '2P', '3P', '4P'];
    const idx = order.indexOf(this.currentMode);
    this.currentMode = order[(idx + 1) % order.length];
    const labels: Record<GameMode, string> = {
      '1P': '1 PLAYER',
      '2P': '2 PLAYERS',
      '3P': '3 PLAYERS',
      '4P': '4 PLAYERS',
    };
    this.modeText.setText(labels[this.currentMode]);

    // Visual feedback
    this.modeText.setScale(1.2);
    this.tweens.add({
      targets: this.modeText,
      scale: 1,
      duration: 150,
    });
  }

  private createSaveSlotDisplay(): void {
    const slotName = SaveManager.getCurrentSlotName();
    const progress = ProgressionSystem.getTotalStars();
    
    // Save slot info box (right side, vertically centered with logo)
    const boxY = 358;
    const box = this.add.rectangle(MENU_PANEL_X, boxY, 300, 50, COLORS.BG_MEDIUM, 0.8);
    box.setStrokeStyle(1, COLORS.DEEP_AMBER);

    // Current save label
    this.add.text(MENU_PANEL_X, boxY - 12, `Save: ${slotName}`, {
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Progress
    const progressText = progress.earned > 0
      ? `★ ${progress.earned} stars | ${SaveManager.getAllSlots()[0]?.completedScenarios ?? 0} levels`
      : 'New Adventure';

    this.saveSlotText = this.add.text(MENU_PANEL_X, boxY + 10, progressText, {
      fontSize: '14px',
      color: COLORS_HEX.TEXT_SECONDARY,
    });
    this.saveSlotText.setOrigin(0.5);
  }

  private createButtons(): void {
    const buttonStartY = 558; // Vertically centered with logo
    const buttonSpacing = 55;

    this.buttons = [
      new UIButton(this, {
        x: MENU_PANEL_X,
        y: buttonStartY,
        text: 'CONTINUE',
        onClick: () => this.startGame(),
      }),
      new UIButton(this, {
        x: MENU_PANEL_X,
        y: buttonStartY + buttonSpacing,
        text: 'LOAD SAVE',
        onClick: () => this.showLoadOverlay(),
      }),
      new UIButton(this, {
        x: MENU_PANEL_X,
        y: buttonStartY + buttonSpacing * 2,
        text: 'DOWNLOAD SAVE',
        onClick: () => this.downloadSave(),
      }),
      new UIButton(this, {
        x: MENU_PANEL_X,
        y: buttonStartY + buttonSpacing * 3,
        text: 'UPLOAD SAVE',
        onClick: () => this.uploadSave(),
      }),
      new UIButton(this, {
        x: MENU_PANEL_X,
        y: buttonStartY + buttonSpacing * 4,
        text: 'NEW GAME',
        onClick: () => this.showNewGameDialog(),
      }),
      new UIButton(this, {
        x: MENU_PANEL_X,
        y: buttonStartY + buttonSpacing * 5,
        text: 'SETTINGS',
        onClick: () => SceneRouter.goToSettings(SCENES.MENU),
      }),
      new UIButton(this, {
        x: MENU_PANEL_X,
        y: buttonStartY + buttonSpacing * 6,
        text: 'CONTROLS',
        onClick: () => this.showControlsOverlay(),
      }),
    ];
  }

  private showControlsOverlay(): void {
    if (this.controlsOverlay) return;

    const centerX = DESIGN_WIDTH / 2;
    const centerY = DESIGN_HEIGHT / 2;

    const lines: string[] = [
      '——— GAMEPAD ———',
      'Left stick / D-pad — Move',
      'A (Cross) — Interact / Build / Upgrade tower',
      'B (Circle) — Cancel',
      'X (Square) — Cycle tower (prev)',
      'Y (Triangle) — Sell tower',
      'L bumper — Cycle tower (prev)',
      'R bumper — Next wave',
      'ZL (L trigger) — Cycle tower (prev)',
      'ZR (R trigger) — Cycle tower (next)',
      'Start — Start',
      'Back/Select — Pause',
      '',
      '——— KEYBOARD (P1) ———',
      'W A S D — Move',
      'E / Space — Interact',
      'Q / Shift — Cancel',
      'Esc — Pause',
      'R — Cycle tower (next)',
      'F — Cycle tower (prev)',
      'Enter — Start',
      'G — Dance',
      'T — Upgrade tower',
      'X — Sell tower',
      'N — Next wave',
      'Mouse left — Interact',
      'Mouse right — Cancel',
      '',
      '——— KEYBOARD (P2) ———',
      'Arrow keys — Move',
      'Numpad 0 / Enter — Interact',
      'Shift (right) — Cancel',
      'Backspace — Pause',
      'Numpad + — Cycle next',
      'Numpad - — Cycle prev',
      'Numpad . — Dance',
      'Numpad * — Upgrade',
    ];
    const lineHeight = 18;
    const titleBarHeight = 50;
    const backButtonArea = 70;
    const contentHeight = lines.length * lineHeight + titleBarHeight + backButtonArea;

    this.controlsOverlay = this.add.container(0, 0);
    this.controlsOverlay.setDepth(DEPTH.OVERLAY);

    const dimBg = this.add.rectangle(centerX, centerY, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS.VOLCANIC_CHARCOAL, 0.85);
    dimBg.setInteractive();
    dimBg.on('pointerdown', () => this.hideControlsOverlay());
    this.controlsOverlay.add(dimBg);

    this.controlsPanel = new UIPanel(this, {
      x: centerX,
      y: centerY,
      width: 520,
      height: contentHeight,
      title: 'CONTROLS',
    });
    this.controlsPanel.setDepth(DEPTH.OVERLAY + 1);
    this.controlsOverlay.add(this.controlsPanel);

    const startY = centerY - contentHeight / 2 + titleBarHeight;
    lines.forEach((line, i) => {
      const text = this.add.text(centerX, startY + i * lineHeight, line, {
        fontSize: '14px',
        color: line.startsWith('———') ? '#aaaaaa' : '#ffffff',
      });
      text.setOrigin(0.5, 0);
      text.setDepth(DEPTH.OVERLAY + 2);
      this.controlsOverlay!.add(text);
    });

    const backButtonY = centerY + contentHeight / 2 - backButtonArea / 2 - 10;
    this.controlsBackButton = new UIButton(this, {
      x: centerX,
      y: backButtonY,
      text: 'Back',
      onClick: () => this.hideControlsOverlay(),
    });
    this.controlsBackButton.setDepth(DEPTH.OVERLAY + 2);
    this.controlsBackButton.setSelected(true);
    this.controlsOverlay.add(this.controlsBackButton);
  }

  private hideControlsOverlay(): void {
    if (this.controlsOverlay) {
      this.controlsOverlay.destroy();
      this.controlsOverlay = undefined;
      this.controlsPanel = undefined;
      this.controlsBackButton = undefined;
    }
  }

  private downloadSave(): void {
    const json = SaveManager.exportSaveData();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tiki-glitch-island-save-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private uploadSave(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result as string;
        if (SaveManager.importSaveData(text)) {
          ProgressState.reload();
          this.scene.restart();
        } else {
          window.alert('Invalid save file.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  private showLoadOverlay(): void {
    if (this.loadOverlay) return;

    const slots = SaveManager.getAllSlots();
    
    // Create overlay container
    this.loadOverlay = this.add.container(0, 0);
    this.loadOverlay.setDepth(DEPTH.OVERLAY);

    // Dim background
    const dimBg = this.add.rectangle(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS.VOLCANIC_CHARCOAL, 0.85);
    dimBg.setInteractive();
    this.loadOverlay.add(dimBg);

    // Panel (Volcanic Charcoal frame, Tiki Gold border)
    const panelWidth = 450;
    const panelHeight = 400;
    const panel = this.add.rectangle(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, panelWidth, panelHeight, COLORS.BG_DARK);
    panel.setStrokeStyle(2, COLORS.PRIMARY);
    this.loadOverlay.add(panel);

    // Title
    const title = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 170, 'LOAD SAVE', {
      fontSize: '28px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.loadOverlay.add(title);

    // Save slots list
    const startY = DESIGN_HEIGHT / 2 - 120;
    const slotHeight = 60;

    slots.slice(0, 5).forEach((slot, index) => {
      const y = startY + index * slotHeight;
      const isCurrentSlot = slot.id === SaveManager.getCurrentSlotId();

      // Slot background
      const slotBg = this.add.rectangle(DESIGN_WIDTH / 2, y, panelWidth - 40, 50, 
        isCurrentSlot ? COLORS.BG_LIGHT : COLORS.BG_MEDIUM);
      slotBg.setStrokeStyle(1, isCurrentSlot ? COLORS.PRIMARY : COLORS.DEEP_AMBER);
      slotBg.setInteractive({ useHandCursor: true });
      this.loadOverlay!.add(slotBg);

      // Slot name
      const nameText = this.add.text(DESIGN_WIDTH / 2 - 180, y - 10, slot.name, {
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
      });
      nameText.setOrigin(0, 0.5);
      this.loadOverlay!.add(nameText);

      // Slot info
      const lastPlayed = slot.lastPlayed ? new Date(slot.lastPlayed).toLocaleDateString() : 'Never';
      const infoText = this.add.text(DESIGN_WIDTH / 2 - 180, y + 12, 
        `${slot.completedScenarios} levels | ★ ${slot.totalStars} | Last: ${lastPlayed}`, {
        fontSize: '12px',
        color: COLORS_HEX.TEXT_SECONDARY,
      });
      infoText.setOrigin(0, 0.5);
      this.loadOverlay!.add(infoText);

      // Current indicator (Glitch Cyan - special UI)
      if (isCurrentSlot) {
        const current = this.add.text(DESIGN_WIDTH / 2 + 180, y, '✓', {
          fontSize: '24px',
          color: COLORS_HEX.GLITCH_CYAN,
        });
        current.setOrigin(1, 0.5);
        this.loadOverlay!.add(current);
      }

      // Load on click
      slotBg.on('pointerdown', () => {
        this.loadSave(slot.id);
      });

      slotBg.on('pointerover', () => {
        slotBg.setStrokeStyle(2, COLORS.ACCENT);
      });

      slotBg.on('pointerout', () => {
        slotBg.setStrokeStyle(1, isCurrentSlot ? COLORS.PRIMARY : COLORS.DEEP_AMBER);
      });
    });

    // Close button (UIButton is a Container, add it directly)
    const closeBtn = new UIButton(this, {
      x: DESIGN_WIDTH / 2,
      y: DESIGN_HEIGHT / 2 + 160,
      text: 'CLOSE',
      onClick: () => this.hideLoadOverlay(),
    });
    this.loadOverlay.add(closeBtn);
  }

  private hideLoadOverlay(): void {
    if (this.loadOverlay) {
      this.loadOverlay.destroy();
      this.loadOverlay = undefined;
    }
  }

  private loadSave(slotId: string): void {
    SaveManager.loadSlot(slotId);
    ProgressState.reload();
    this.hideLoadOverlay();
    // Refresh the scene
    this.scene.restart();
  }

  private showNewGameDialog(): void {
    // Use browser prompt for simplicity (can be replaced with in-game dialog)
    const name = window.prompt('Enter a name for your new save:', `Save ${SaveManager.getAllSlots().length}`);
    
    if (name && name.trim()) {
      SaveManager.startNewGame(name.trim());
      ProgressState.reload();
      this.scene.restart();
    }
  }

  private setupChaseAnimation(): void {
    this.time.delayedCall(CHASE_FIRST_DELAY_MS, () => this.runChaseAnimation());
  }

  private runChaseAnimation(): void {
    const rightToLeft = this.chaseNextDirection === 1;
    this.chaseNextDirection *= -1;

    const margin = CHASE_OFFSCREEN_MARGIN;
    const startX = rightToLeft ? DESIGN_WIDTH + margin : -margin;
    const endX = rightToLeft ? -margin : DESIGN_WIDTH + margin;
    const monsterOffset = rightToLeft ? CHASE_MONSTER_START_OFFSET : -CHASE_MONSTER_START_OFFSET;
    const monsterStartX = startX + monsterOffset;
    const monsterEndX = endX; // Same as player so both go fully off screen

    // Pick random monster type (that has textures loaded)
    const available = CHASE_MONSTER_TYPES.filter(
      (m) => this.textures.exists(`monster-${m.type}-1`)
    );
    const chosen = available.length > 0
      ? available[Math.floor(Math.random() * available.length)]
      : CHASE_MONSTER_TYPES[0];
    this.chaseMonsterType = chosen.type;
    this.chaseMonsterFrameCount = chosen.frameCount;

    // Pick a random player (1–4) for this chase; fallback to player 1 if textures missing
    const shuffled = [...CHASE_PLAYER_NUMBERS].sort(() => Math.random() - 0.5);
    let runKeys = getChasePlayerRunKeys(1);
    for (const n of shuffled) {
      const keys = getChasePlayerRunKeys(n);
      if (this.textures.exists(keys[0])) {
        runKeys = keys;
        break;
      }
    }
    this.chasePlayerRunKeys = runKeys;

    // Player sprite (running)
    const playerKey = this.chasePlayerRunKeys[0];
    if (!this.textures.exists(playerKey)) return;
    this.chasePlayerSprite = this.add.image(startX, CHASE_Y, playerKey);
    this.chasePlayerSprite.setScale(CHASE_PLAYER_SCALE);
    this.chasePlayerSprite.setFlipX(rightToLeft);
    this.chasePlayerSprite.setDepth(CHASE_DEPTH_BEHIND_UI);
    this.chasePlayerFrameTimer = 0;
    this.chasePlayerFrameIndex = 0;

    // Monster sprite (walking)
    const monsterKey = `monster-${this.chaseMonsterType}-1`;
    if (!this.textures.exists(monsterKey)) {
      this.chasePlayerSprite.destroy();
      this.chasePlayerSprite = null;
      this.scheduleNextChase();
      return;
    }
    this.chaseMonsterSprite = this.add.image(monsterStartX, CHASE_Y, monsterKey);
    this.chaseMonsterSprite.setScale(CHASE_MONSTER_SCALE);
    this.chaseMonsterSprite.setFlipX(rightToLeft);
    this.chaseMonsterSprite.setDepth(CHASE_DEPTH_BEHIND_UI);
    this.chaseMonsterFrameTimer = 0;
    this.chaseMonsterFrameIndex = 0;

    const duration = CHASE_DURATION_MS;
    this.tweens.add({
      targets: this.chasePlayerSprite,
      x: endX,
      duration,
      ease: 'Linear',
      onComplete: () => {
        this.chasePlayerSprite?.destroy();
        this.chasePlayerSprite = null;
        this.chaseMonsterSprite?.destroy();
        this.chaseMonsterSprite = null;
        this.scheduleNextChase();
      },
    });
    this.tweens.add({
      targets: this.chaseMonsterSprite,
      x: monsterEndX,
      duration: duration * CHASE_MONSTER_DURATION_MULT, // Faster so monster gains on player
      ease: 'Linear',
    });
  }

  private scheduleNextChase(): void {
    this.time.delayedCall(CHASE_INTERVAL_MS, () => this.runChaseAnimation());
  }

  private createAccountButton(): void {
    // Position in top-left corner
    this.accountContainer = this.add.container(20, 20);
    this.accountContainer.setDepth(DEPTH.UI_ELEMENTS);

    this._updateAccountButton();

    // Listen for auth state changes to update the button
    this.authUnsubscribe = AuthService.onChange(() => {
      this._updateAccountButton();
    });
  }

  private _updateAccountButton(): void {
    if (!this.accountContainer) return;

    // Clear previous children
    this.accountContainer.removeAll(true);
    this.accountProfileImage = undefined;
    this.accountText = undefined;
    this.loginDancerSprite = null;

    const user = AuthService.user;

    if (user) {
      // ── Logged in: show profile picture + display name (left-aligned) ──
      const avatarSize = 36;

      // Profile picture background circle
      const circleBg = this.add.circle(avatarSize / 2, avatarSize / 2, avatarSize / 2 + 2, COLORS.PRIMARY);
      this.accountContainer.add(circleBg);

      // Load profile picture
      if (user.picture_url) {
        const textureKey = `profile-pic-${user.id}`;
        if (this.textures.exists(textureKey)) {
          this._showProfileImage(textureKey, avatarSize);
        } else {
          this._showProfileInitial(user.display_name || user.email, avatarSize);
          this.load.image(textureKey, user.picture_url);
          this.load.once('complete', () => {
            if (this.accountContainer && this.textures.exists(textureKey)) {
              if (this.accountText) {
                this.accountText.destroy();
                this.accountText = undefined;
              }
              this._showProfileImage(textureKey, avatarSize);
            }
          });
          this.load.start();
        }
      } else {
        this._showProfileInitial(user.display_name || user.email, avatarSize);
      }

      // Display name text
      const nameText = this.add.text(avatarSize + 10, avatarSize / 2, user.display_name || 'Set Name', {
        fontSize: '14px',
        color: user.display_name ? '#ffffff' : COLORS_HEX.TIKI_GOLD,
        fontStyle: 'bold',
      });
      nameText.setOrigin(0, 0.5);
      this.accountContainer.add(nameText);

      // Hit area
      const totalWidth = avatarSize + 10 + nameText.width;
      const hitArea = this.add.rectangle(
        totalWidth / 2,
        avatarSize / 2,
        totalWidth + 8,
        avatarSize + 8,
        0x000000,
        0
      );
      hitArea.setInteractive({ useHandCursor: true });
      hitArea.on('pointerover', () => nameText.setColor(COLORS_HEX.NEON_ICE));
      hitArea.on('pointerout', () => nameText.setColor(user.display_name ? '#ffffff' : COLORS_HEX.TIKI_GOLD));
      hitArea.on('pointerdown', () => {
        AuthUI.showProfile(() => this._updateAccountButton());
      });
      this.accountContainer.add(hitArea);

    } else {
      // ── Not logged in: show dancing player 1 + "Login" (left-aligned) ──
      const iconSize = 36;
      const danceKeys = ['player-1-dance-1', 'player-1-dance-2', 'player-1-dance-3', 'player-1-dance-4'];
      const hasDance = danceKeys.every((k) => this.textures.exists(k));

      if (hasDance) {
        this.loginDancerSprite = this.add.image(iconSize / 2, iconSize / 2, 'player-1-dance-1');
        this.loginDancerSprite.setDisplaySize(iconSize, iconSize);
        this.accountContainer.add(this.loginDancerSprite);
        this.loginDancerFrameIndex = 0;
        this.loginDancerTimer = 0;
        this.loginDancerCycleCount = 0;
        this.loginDancerFlipped = false;
      } else {
        this.loginDancerSprite = null;
      }

      const text = this.add.text(iconSize + 10, iconSize / 2, 'Login', {
        fontSize: '14px',
        color: COLORS_HEX.TEXT_SECONDARY,
      });
      text.setOrigin(0, 0.5);
      this.accountContainer.add(text);

      // Hit area
      const totalWidth = iconSize + 10 + text.width;
      const hitArea = this.add.rectangle(
        totalWidth / 2,
        iconSize / 2,
        totalWidth + 8,
        iconSize + 8,
        0x000000,
        0
      );
      hitArea.setInteractive({ useHandCursor: true });
      hitArea.on('pointerover', () => text.setColor(COLORS_HEX.TIKI_GOLD));
      hitArea.on('pointerout', () => text.setColor(COLORS_HEX.TEXT_SECONDARY));
      hitArea.on('pointerdown', () => {
        AuthUI.showSignIn(() => this._updateAccountButton());
      });
      this.accountContainer.add(hitArea);
    }
  }

  private _showProfileImage(textureKey: string, size: number): void {
    if (!this.accountContainer) return;
    const cx = size / 2;
    const img = this.add.image(cx, size / 2, textureKey);
    img.setDisplaySize(size, size);
    const maskShape = this.add.graphics();
    maskShape.fillCircle(cx, size / 2, size / 2);
    maskShape.setVisible(false);
    img.setMask(maskShape.createGeometryMask());
    this.accountContainer.add(img);
    this.accountProfileImage = img;
  }

  private _showProfileInitial(name: string, size: number): void {
    if (!this.accountContainer) return;
    const cx = size / 2;
    const initial = (name || '?')[0].toUpperCase();
    const bg = this.add.circle(cx, size / 2, size / 2, COLORS.BG_LIGHT);
    this.accountContainer.add(bg);
    const text = this.add.text(cx, size / 2, initial, {
      fontSize: '18px',
      color: COLORS_HEX.TIKI_GOLD,
      fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    this.accountContainer.add(text);
    this.accountText = text;
  }

  private createControllerIndicator(): void {
    // Show connected gamepads
    const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
    let connectedCount = 0;

    for (const gp of gamepads) {
      if (gp && gp.connected) connectedCount++;
    }

    const indicatorText = connectedCount > 0
      ? `🎮 ${connectedCount} controller${connectedCount > 1 ? 's' : ''} connected`
      : '🎮 No controllers detected';

    const indicator = this.add.text(DESIGN_WIDTH - 20, 20, indicatorText, {
      fontSize: '12px',
      color: connectedCount > 0 ? COLORS_HEX.GLITCH_CYAN : COLORS_HEX.TEXT_MUTED,
    });
    indicator.setOrigin(1, 0);

    // Lower-right warning when no controller detected (main menu only)
    const warningWidth = 320;
    const warningPadding = 14;
    const warningX = DESIGN_WIDTH - warningPadding - warningWidth / 2;
    const warningY = DESIGN_HEIGHT - 80;

    this.controllerWarningContainer = this.add.container(warningX, warningY);
    const warningBg = this.add.rectangle(0, 0, warningWidth + warningPadding * 2, 110, COLORS.BG_DARK, 0.92);
    warningBg.setStrokeStyle(2, COLORS.DEEP_AMBER);
    this.controllerWarningContainer.add(warningBg);

    const title = this.add.text(0, -28, 'Please connect a controller', {
      fontSize: '14px',
      color: COLORS_HEX.TIKI_GOLD,
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.controllerWarningContainer.add(title);

    const hint = this.add.text(0, 0, 'This game is best played with one or more\ncontrollers. Keyboard works but is not recommended.', {
      fontSize: '11px',
      color: COLORS_HEX.TEXT_MUTED,
      align: 'center',
      lineSpacing: 4,
    });
    hint.setOrigin(0.5);
    this.controllerWarningContainer.add(hint);

    this.controllerWarningContainer.setVisible(connectedCount === 0);
    this.controllerWarningContainer.setDepth(DEPTH.OVERLAY);

    // Update periodically
    this.time.addEvent({
      delay: 2000,
      loop: true,
      callback: () => {
        const gps = navigator.getGamepads ? navigator.getGamepads() : [];
        let count = 0;
        for (const gp of gps) {
          if (gp && gp.connected) count++;
        }
        const text = count > 0
          ? `🎮 ${count} controller${count > 1 ? 's' : ''} connected`
          : '🎮 No controllers detected';
        indicator.setText(text);
        indicator.setColor(count > 0 ? COLORS_HEX.GLITCH_CYAN : COLORS_HEX.TEXT_MUTED);
        if (this.controllerWarningContainer) {
          this.controllerWarningContainer.setVisible(count === 0);
        }
      },
    });
  }

  private updateSelection(): void {
    this.buttons.forEach((btn, i) => {
      btn.setSelected(i === this.selectedIndex);
    });
  }

  private navigateUp(): void {
    this.selectedIndex = Math.max(0, this.selectedIndex - 1);
    this.updateSelection();
  }

  private navigateDown(): void {
    this.selectedIndex = Math.min(this.buttons.length - 1, this.selectedIndex + 1);
    this.updateSelection();
  }

  private confirmSelection(): void {
    this.buttons[this.selectedIndex].click();
  }

  private startGame(): void {
    // Set game mode
    GameState.setMode(this.currentMode);

    // Get next recommended scenario
    const nextScenario = ProgressionSystem.getNextRecommendedScenario();

    if (nextScenario) {
      GameState.setSelectedScenario(nextScenario);
    }

    // Go to map
    SceneRouter.goToMap(SCENES.MENU);
  }

  shutdown(): void {
    // Clean up auth listener when leaving the scene
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = undefined;
    }
  }

  /** Run chase + login dancer animations (called even when auth modal is open) */
  private _updateAnimations(delta: number): void {
    // Chase animation frame stepping (player run + monster walk)
    if (this.chasePlayerSprite && this.chaseMonsterSprite) {
      this.chasePlayerFrameTimer += delta;
      if (this.chasePlayerFrameTimer >= PLAYER_FRAME_MS) {
        this.chasePlayerFrameTimer = 0;
        this.chasePlayerFrameIndex = (this.chasePlayerFrameIndex + 1) % this.chasePlayerRunKeys.length;
        this.chasePlayerSprite.setTexture(this.chasePlayerRunKeys[this.chasePlayerFrameIndex]);
      }
      this.chaseMonsterFrameTimer += delta;
      if (this.chaseMonsterFrameTimer >= MONSTER_FRAME_MS) {
        this.chaseMonsterFrameTimer = 0;
        this.chaseMonsterFrameIndex = (this.chaseMonsterFrameIndex + 1) % this.chaseMonsterFrameCount;
        const key = `monster-${this.chaseMonsterType}-${this.chaseMonsterFrameIndex + 1}`;
        if (this.textures.exists(key)) this.chaseMonsterSprite.setTexture(key);
      }
      const centerX = DESIGN_WIDTH / 2;
      const halfWidth = DESIGN_WIDTH / 2;
      for (const sprite of [this.chasePlayerSprite, this.chaseMonsterSprite]) {
        const t = (sprite.x - centerX) / halfWidth;
        const brightness = Math.max(0.08, 1 - t * t);
        sprite.setAlpha(brightness);
      }
    }

    // Login button: BPM-synced dance (same as map selector dancer)
    if (this.loginDancerSprite && this.loginDancerSprite.scene === this) {
      const bpm = AudioRouter.getCurrentBPM();
      const beatMs = 60000 / bpm;
      const frameMs = beatMs / MenuScene.LOGIN_DANCE_FRAMES_PER_CYCLE;
      this.loginDancerTimer += delta;
      if (this.loginDancerTimer >= frameMs) {
        this.loginDancerTimer -= frameMs;
        this.loginDancerFrameIndex++;
        if (this.loginDancerFrameIndex >= MenuScene.LOGIN_DANCE_FRAMES_PER_CYCLE) {
          this.loginDancerFrameIndex = 0;
          this.loginDancerCycleCount++;
          if (this.loginDancerCycleCount >= MenuScene.LOGIN_DANCE_CYCLES_BEFORE_FLIP) {
            this.loginDancerCycleCount = 0;
            this.loginDancerFlipped = !this.loginDancerFlipped;
            this.loginDancerSprite.setFlipX(this.loginDancerFlipped);
          }
        }
        const frameKey = `player-1-dance-${this.loginDancerFrameIndex + 1}`;
        if (this.textures.exists(frameKey)) this.loginDancerSprite.setTexture(frameKey);
      }
    }
  }

  update(_time: number, delta: number): void {
    InputRouter.update();

    // When auth overlay (HTML modal) is open, block ALL game input but keep animations going
    if (AuthUI.isVisible) {
      this._updateAnimations(delta);
      InputRouter.lateUpdate();
      return;
    }

    // When controls overlay is open, only handle Back / Cancel
    if (this.controlsOverlay) {
      if (InputRouter.anyPlayerJustPressed('INTERACT') || InputRouter.anyPlayerJustPressed('START')) {
        this.controlsBackButton?.click();
      }
      if (InputRouter.anyPlayerJustPressed('CANCEL') || InputRouter.anyPlayerJustPressed('PAUSE')) {
        this.hideControlsOverlay();
      }
      InputRouter.lateUpdate();
      return;
    }

    this._updateAnimations(delta);

    // Navigation
    if (InputRouter.anyPlayerJustPressed('MOVE_UP')) {
      this.navigateUp();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_DOWN')) {
      this.navigateDown();
    }

    // Mode switching
    if (InputRouter.anyPlayerJustPressed('MOVE_LEFT') || InputRouter.anyPlayerJustPressed('MOVE_RIGHT')) {
      this.toggleMode();
    }

    // Confirm (ignore for a short delay after menu open to avoid carrying over a previous button press)
    if (this.time.now >= this.menuInputReadyAt && (InputRouter.anyPlayerJustPressed('INTERACT') || InputRouter.anyPlayerJustPressed('START'))) {
      this.confirmSelection();
    }

    // Must call lateUpdate at end of frame to properly track "just pressed" state
    InputRouter.lateUpdate();
  }
}
